import { isReduced, OPTIC, RECEDE, SPRING_UI, TRAVEL } from './motion';

/**
 * The opening: an authored sequence that plays on every page load (or when
 * deliberately replayed). It is built from Web Animations on one shared
 * clock, so it can be finished in an instant, cancelled cleanly, or paused
 * and scrubbed frame by frame for review.
 *
 *  A  quiet stage      a tinted veil lifts; light gathers from above
 *  B  the wordmark     My.txt comes into focus behind a soft frontier
 *                      (sharp, glow and trace layers), each glyph settling
 *  C  the claim        word by word; *editor* catches the light and its
 *                      underline draws itself
 *  D  hold             the identity, perfectly still
 *  E  relocation       the mark travels to its place at the bottom left
 *  F  the window       resolves in the centre; its controls, the invitation,
 *                      the caret and the three ways in arrive in order
 *
 * Over a restored session, F is the work itself: every restored window rises
 * into its own place, back to front, the focused one last and on the beat
 * where a first window would arrive. Nothing is rebuilt or stood in for —
 * the windows are only held invisible (from the first frame) until then.
 *
 * Every final keyframe equals an element's resting style, so ending the
 * sequence — at any moment — leaves exactly the everyday interface.
 */

export interface IntroTargets {
  /** The stage: veil and light. */
  stage: HTMLElement | null;
  /** The travelling mark, laid out at its hero size. */
  brand: HTMLElement | null;
  /** Where the mark comes to rest; null when it should recede instead (compact screens, a covered corner). */
  landing: DOMRect | null;
  /** How strongly the landing place shows the mark (it yields beside a window). */
  landingOpacity?: number;
  /** The focused window's frame. */
  frame: HTMLElement | null;
  /** Every other restored window's frame, back to front. */
  others?: HTMLElement[];
  /** The landing place shows the claim too (narrow canvases keep only the wordmark). */
  landingClaim: boolean;
  /** A compact screen: the page rises under the mark, which then settles into it. */
  compact: boolean;
}

export interface Timeline {
  readonly duration: number;
  readonly finished: Promise<void>;
  /** Jump to the end state. */
  finish(): void;
  /** Drop every animation without resolving (an effect being re-run). */
  cancel(): void;
  /** Aim the travelling mark at a new landing place (fonts settled, layout moved). */
  retarget(landing: DOMRect): void;
  /** Freeze at a moment on the timeline (ms), for frame-by-frame review. */
  seek(ms: number): void;
  pause(): void;
  play(): void;
  now(): number;
}

/** Moments on the clock, in ms. Phases overlap; nothing waits on anything. */
export const TIMING = {
  veil: 0,
  trace: 80,
  word: 180,
  wordSweep: 1000,
  claim: 760,
  claimSweep: 760,
  editorGlow: 1240,
  underline: 1300,
  underlineDraw: 560,
  travel: 2180,
  travelDur: 900,
  window: 2560,
  windowDur: 1050,
  controls: 2920,
  invitation: 3020,
  caret: 3520,
  tray: 3360,
  drop: 3700,
  end: 4200,
};

export type Timing = typeof TIMING;

/** On a compact screen the window is the page: it rises during the hold, so the mark can settle into it. */
export function timing(compact: boolean): Timing {
  return compact ? { ...TIMING, window: 1640, windowDur: 900 } : TIMING;
}

const FRONTIER = 'cubic-bezier(0.32, 0.06, 0.18, 1)';
const SETTLE = 'cubic-bezier(0.16, 0.84, 0.24, 1)';

export function runIntro(t: IntroTargets): Timeline {
  const all: Animation[] = [];
  const add: Add = (el, frames, delay, duration, easing = OPTIC, pseudoElement) => {
    if (!el) return null;
    const a = el.animate(frames, { delay, duration, easing, fill: 'both', pseudoElement });
    a.id = 'mytxt-intro';
    all.push(a);
    return a;
  };
  const T = timing(t.compact);
  let retarget: ((landing: DOMRect) => void) | null = null;

  if (!isReduced()) {
    stage(add, t, T);
    retarget = brand(add, t, T);
    windowParts(add, t, T);
  }

  const duration = all.reduce((m, a) => Math.max(m, Number(a.effect?.getComputedTiming().endTime ?? 0)), 0);
  const finished = Promise.all(all.map((a) => a.finished)).then(
    () => undefined,
    () => undefined,
  );
  let paused = false;

  return {
    duration,
    finished,
    finish() {
      for (const a of all) {
        try {
          a.finish();
        } catch {
          // An animation that cannot finish (cancelled) has nothing left to show.
        }
      }
    },
    cancel() {
      for (const a of all) a.cancel();
    },
    retarget(landing) {
      if (landing.width) retarget?.(landing);
    },
    seek(ms) {
      paused = true;
      for (const a of all) {
        a.pause();
        a.currentTime = Math.max(0, Math.min(ms, duration));
      }
    },
    pause() {
      paused = true;
      for (const a of all) a.pause();
    },
    play() {
      if (!paused) return;
      paused = false;
      // play() would rewind an animation that has reached its end; those simply stay finished.
      for (const a of all) {
        if (Number(a.currentTime) >= Number(a.effect?.getComputedTiming().endTime ?? 0)) a.finish();
        else a.play();
      }
    },
    now: () => (all.length ? Math.max(...all.map((a) => Number(a.currentTime ?? 0))) : duration),
  };
}

type Add = (el: Element | null | undefined, frames: Keyframe[], delay: number, duration: number, easing?: string, pseudo?: string) => Animation | null;

function stage(add: Add, t: IntroTargets, T: Timing) {
  const s = t.stage;
  if (!s) return;
  // A darker, quieter canvas lifts like a veil, and light gathers from above.
  add(s.querySelector('.intro-veil'), [{ opacity: 1 }, { opacity: 0 }], T.veil, 1500, 'cubic-bezier(0.4, 0, 0.2, 1)');
  add(s.querySelector('.intro-light'), [{ opacity: 0, transform: 'scale(1.18)' }, { opacity: 1, transform: 'scale(1)' }], T.veil + 100, 1900, SETTLE);
}

/** A focus frontier: the mask slides from fully hidden (100%) to fully shown (0%). */
const sweep = (from = '100% 0', to = '0% 0'): Keyframe[] => [
  { maskPosition: from, webkitMaskPosition: from },
  { maskPosition: to, webkitMaskPosition: to },
];

function brand(add: Add, t: IntroTargets, T: Timing): ((landing: DOMRect) => void) | null {
  const b = t.brand;
  if (!b) return null;
  // Measured before anything moves it: the mark at its hero place and size.
  const from = b.getBoundingClientRect();
  const word = b.querySelector('[data-part="word"]');
  const claim = b.querySelector('[data-part="claim"]');
  const layer = (part: Element | null, name: string) => part?.querySelector(`[data-layer="${name}"]`);

  // B. A faint latent image of the mark, then the sharp letters behind a moving frontier.
  add(layer(word, 'trace'), [{ opacity: 0, filter: 'blur(18px)' }, { opacity: 0.11, filter: 'blur(7px)', offset: 0.55 }, { opacity: 0, filter: 'blur(4px)' }], T.trace, 1700, 'ease-out');
  add(layer(word, 'sharp'), sweep(), T.word, T.wordSweep, FRONTIER);
  add(layer(word, 'glow'), [...sweep('100% 0', '0% 0').map((k, i) => ({ ...k, opacity: i ? 0 : 0.85 }))], T.word, T.wordSweep * 1.12, FRONTIER);
  // Each glyph settles as the frontier passes it: a small rise, no change of shape.
  word?.querySelectorAll<SVGPathElement>('[data-layer="sharp"] .brand-glyph').forEach((g) => {
    const x = Number(g.dataset.x ?? 0);
    add(g, [{ transform: 'translateY(16px)' }, { transform: 'translateY(0px)' }], T.word + x * T.wordSweep * 0.62, 900, SETTLE);
  });

  // C. The claim reads in, word by word.
  add(layer(claim, 'trace'), [{ opacity: 0, filter: 'blur(14px)' }, { opacity: 0.1, filter: 'blur(6px)', offset: 0.5 }, { opacity: 0, filter: 'blur(3px)' }], T.claim - 120, 1300, 'ease-out');
  add(layer(claim, 'sharp'), sweep(), T.claim, T.claimSweep, FRONTIER);
  claim?.querySelectorAll<SVGGElement>('[data-layer="sharp"] .brand-claim-word').forEach((w, i) => {
    add(w, [{ transform: 'translateY(12px)', opacity: 0.2 }, { transform: 'translateY(0px)', opacity: 1 }], T.claim + i * 95, 760, SETTLE);
  });
  // *editor* catches the light while its underline draws — the claim's one emphasis, stated once.
  add(layer(claim, 'glow'), [{ opacity: 0 }, { opacity: 0.55, offset: 0.4 }, { opacity: 0 }], T.editorGlow, 1000, 'ease-in-out');
  add(claim?.querySelector('[data-layer="sharp"] [data-editor]'), [{ transform: 'translateY(0px) skewX(0deg)' }, { transform: 'translateY(-2px) skewX(-3deg)', offset: 0.35 }, { transform: 'translateY(0px) skewX(0deg)' }], T.editorGlow, 900, 'cubic-bezier(0.3, 0, 0.2, 1)');
  claim?.querySelectorAll('.brand-underline').forEach((u) => {
    add(u, [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], T.underline, T.underlineDraw, 'cubic-bezier(0.55, 0, 0.15, 1)');
  });

  // E. Relocation: one object travels to its corner, handing over its scale and its colour.
  if (t.landing && from.width) {
    const ink = t.compact ? 'var(--ink)' : 'var(--brand-ink)';
    // Beside a window the corner mark yields; the travelling one arrives already yielding, so they coincide.
    const opacity = t.landingOpacity ?? 1;
    const frames = (to: DOMRect): Keyframe[] => [
      { transform: 'translate(0px, 0px) scale(1)', color: 'var(--ink)', opacity: 1 },
      { transform: `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${to.width / from.width})`, color: ink, opacity },
    ];
    const travel = add(b, frames(t.landing), T.travel, T.travelDur, TRAVEL);
    // Where the corner keeps only the wordmark, the claim lets go on the way.
    if (!t.landingClaim) add(claim, [{ opacity: 1 }, { opacity: 0 }], T.travel + 120, T.travelDur * 0.6, 'ease-out');
    return (to) => (travel?.effect as KeyframeEffect | null)?.setKeyframes(frames(to));
  }
  add(b, [{ opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' }, { opacity: 0, filter: 'blur(8px)', transform: 'scale(0.96)' }], T.travel, 620, RECEDE);
  return null;
}

/** A window: a machined object settling into place — out of focus, slightly low and small, then exact. */
const ARRIVE: Keyframe[] = [
  { opacity: 0, transform: 'translateY(26px) scale(0.94)', filter: 'blur(14px)' },
  { opacity: 1, filter: 'blur(0.5px)', offset: 0.55 },
  { opacity: 1, transform: 'translateY(0px) scale(1)', filter: 'blur(0px)' },
];

function windowParts(add: Add, t: IntroTargets, T: Timing) {
  // F. Restored windows rise back to front, close together, so the focused one still lands on the window's beat.
  const others = t.others ?? [];
  const step = others.length ? Math.min(70, 320 / others.length) : 0;
  others.forEach((o, i) => add(o, ARRIVE, T.window - (others.length - i) * step, T.windowDur, SETTLE));
  const f = t.frame;
  if (!f) return;
  add(f, ARRIVE, T.window, T.windowDur, SETTLE);
  // Its own details follow (a restored window simply has fewer of them). The resize grip comes with the last.
  add(f.parentElement?.querySelector('.win-grip'), [{ opacity: 0 }, { opacity: 1 }], T.drop, 400, 'ease-out');
  f.querySelectorAll('.wc-dot').forEach((d, i) => add(d, [{ opacity: 0, transform: 'scale(0.2)' }, { opacity: 1, transform: 'scale(1)' }], T.controls + i * 70, SPRING_UI.ms + 120, SPRING_UI.css));
  add(f.querySelector('.head-actions'), [{ opacity: 0, transform: 'translateX(-4px)' }, { opacity: 1, transform: 'translateX(0px)' }], T.controls + 140, 520);
  const text = f.querySelector('.empty-text');
  add(text, [{ ...sweep()[0], filter: 'blur(6px)', opacity: 0.001 }, { opacity: 1, offset: 0.2 }, { ...sweep()[1], filter: 'blur(0px)', opacity: 1 }], T.invitation, 900, FRONTIER);
  add(f.querySelector('.empty-caret'), [{ opacity: 0, transform: 'scaleY(0.2)' }, { opacity: 1, transform: 'scaleY(1)' }], T.caret, 360, SPRING_UI.css);
  add(f.querySelector('.empty-actions'), [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0px)' }], T.tray, 620);
  f.querySelectorAll('.empty-action').forEach((a, i) => add(a, [{ opacity: 0, filter: 'blur(4px)' }, { opacity: 1, filter: 'blur(0px)' }], T.tray + 80 + i * 70, 460));
  add(f.querySelector('.drop-zone'), [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0px)' }], T.drop, 560);
  // The page's own mark takes over from the travelling one only at the very end — they coincide exactly.
  add(f.querySelector('.brand-slot'), [{ opacity: 0 }, { opacity: 0, offset: 0.999 }, { opacity: 1 }], 0, T.end, 'linear');
}
